// PrintPreviewModal.tsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

type PageSize = 'A4' | 'Letter';

interface PrintPreviewModalProps {
  open: boolean;
  title: string;
  onClose: () => void;

  /** Prefer passing the element containing the content to print */
  sourceElem?: HTMLElement | null;

  /** Fallback: pass a pre-rendered HTML string */
  sourceHTML?: string;

  /** Physical page size for printing (used in @page) */
  pageSize?: PageSize;

  /** Printer page margin (mm/in/px). Default: '24px' */
  printMargin?: string;

  /** Padding inside the preview viewport (screen only). Default: '24px' */
  previewPadding?: string;
}

/** Serialize CSS rules from a <style> element (handles Emotion/JSS CSSOM). */
function serializeStyleElement(styleEl: HTMLStyleElement): string {
  const attrs = Array.from(styleEl.attributes)
    .map((a) => `${a.name}="${a.value}"`)
    .join(' ');
  let cssText = styleEl.textContent || '';

  try {
    const sheet = Array.from(document.styleSheets).find(
      (s) => (s as CSSStyleSheet).ownerNode === styleEl,
    ) as CSSStyleSheet | undefined;

    if (sheet && sheet.cssRules) {
      cssText = Array.from(sheet.cssRules)
        .map((r) => r.cssText)
        .join('\n');
    }
  } catch {
    // Cross-origin or restricted stylesheet; ignore (fallback to textContent)
  }

  return `<style ${attrs}>${cssText}</style>`;
}

/** Copy ALL head styles (links + serialized style tags). */
function collectHeadForPrint(): string {
  const parts: string[] = [];

  // 1) Copy stylesheet links as-is
  document.head
    .querySelectorAll('link[rel="stylesheet"]')
    .forEach((link) => parts.push((link as HTMLElement).outerHTML));

  // 2) Serialize every <style> with CSSOM rules where possible
  document.head.querySelectorAll('style').forEach((style) => {
    parts.push(serializeStyleElement(style as HTMLStyleElement));
  });

  return parts.join('\n');
}

/** Build the full HTML for the iframe/popup. Only minimal CSS added. */
function buildHtmlDoc({
  title,
  contentHTML,
  pageSize,
  printMargin,
  previewPadding,
}: {
  title: string;
  contentHTML: string;
  pageSize: PageSize;
  printMargin: string;
  previewPadding: string;
}) {
  const headStyles = collectHeadForPrint();

  const minimalCSS = `
    @media screen {
      html, body { min-height: 100%; height: auto; }
      body { margin: 0; overflow: auto !important; background: #fff; }
      #print-root { padding: ${previewPadding}; box-sizing: border-box; }
    }
    @media print {
      @page { size: ${pageSize}; margin: ${printMargin}; }
      body { margin: 0; }
      #print-root { padding: 0; } /* rely on @page margin in print */
    }
  `;

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <base href="${document.baseURI}" />
  <title>${title.replace(/</g, '&lt;')}</title>
  ${headStyles}
  <style>${minimalCSS}</style>
</head>
<body>
  <div id="print-root">${contentHTML || '<div style="padding:16px;color:#666;">Nothing to print.</div>'}</div>
</body>
</html>`;
}

/** Write html into iframe and wait for stylesheet links & fonts to load. */
async function writeAndWaitForStyles(
  iframe: HTMLIFrameElement,
  html: string,
): Promise<void> {
  await new Promise<void>((res) => {
    iframe.onload = () => res();
    iframe.src = 'about:blank';
  });

  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) return;

  doc.open();
  doc.write(html);
  doc.close();

  const linkEls = Array.from(
    doc.querySelectorAll('link[rel="stylesheet"]'),
  ) as HTMLLinkElement[];

  await Promise.all(
    linkEls.map(
      (l) =>
        new Promise<void>((resolve) => {
          if ((l as any).sheet) return resolve();
          l.addEventListener('load', () => resolve(), { once: true });
          l.addEventListener('error', () => resolve(), { once: true });
        }),
    ),
  );

  try {
    const anyDoc: any = doc;
    if (anyDoc.fonts && typeof anyDoc.fonts.ready?.then === 'function') {
      await anyDoc.fonts.ready;
    }
  } catch {
    // ignore
  }
}

/** Extract printable HTML from a source element or raw HTML. */
function extractPrintableHTML(
  sourceElem?: HTMLElement | null,
  sourceHTML?: string,
): string {
  if (sourceElem) {
    const wrapper = document.createElement('div');

    // Clone only children of the source container
    Array.from(sourceElem.childNodes).forEach((child) => {
      const clone = child.cloneNode(true) as HTMLElement;

      if (clone instanceof HTMLElement) {
        clone
          .querySelectorAll('.button-overlay, [data-no-print="true"], .ref-btn')
          .forEach((n) => n.remove());
      }

      wrapper.appendChild(clone);
    });

    return wrapper.innerHTML;
  }

  if (sourceHTML) {
    const tmp = document.createElement('div');
    tmp.innerHTML = sourceHTML;
    tmp
      .querySelectorAll('.button-overlay, [data-no-print="true"], .ref-btn')
      .forEach((n) => n.remove());
    return tmp.innerHTML;
  }

  return '<div style="padding:16px;color:#666;">Nothing to print.</div>';
}

export default function PrintPreviewModal({
  open,
  title,
  onClose,
  sourceElem,
  sourceHTML,
  pageSize = 'Letter',
  printMargin = '24px',
  previewPadding = '24px',
}: PrintPreviewModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [docHtml, setDocHtml] = useState('');

  // Build HTML when inputs change while modal is open
  useEffect(() => {
    if (!open) return;
    const content = extractPrintableHTML(sourceElem, sourceHTML);
    const html = buildHtmlDoc({
      title,
      contentHTML: content,
      pageSize,
      printMargin,
      previewPadding,
    });
    setDocHtml(html);
  }, [
    open,
    title,
    sourceElem,
    sourceHTML,
    pageSize,
    printMargin,
    previewPadding,
  ]);

  // Load preview into iframe and wait for CSS/Fonts
  useEffect(() => {
    if (!open || !iframeRef.current || !docHtml) return;

    let cancelled = false;

    (async () => {
      try {
        await writeAndWaitForStyles(iframeRef.current!, docHtml);
      } catch (e) {
        console.error('Failed to load print preview:', e);
      } finally {
        if (cancelled && iframeRef.current) {
          iframeRef.current.src = 'about:blank';
        }
      }
    })();

    return () => {
      cancelled = true;
      if (iframeRef.current) {
        iframeRef.current.src = 'about:blank';
      }
    };
  }, [open, docHtml]);

  const handlePrint = async () => {
    const pw = window.open('', '_blank', 'width=800,height=600');
    if (pw) {
      pw.document.open();
      pw.document.write(docHtml);
      pw.document.title = title;
      pw.document.close();

      // Wait for links & fonts in popup too
      const doc = pw.document as any;
      const waitForLinks = () =>
        Promise.all(
          Array.from(
            pw.document.querySelectorAll('link[rel="stylesheet"]'),
          ).map(
            (l: any) =>
              new Promise<void>((resolve) => {
                if (l.sheet) return resolve();
                l.addEventListener('load', () => resolve(), { once: true });
                l.addEventListener('error', () => resolve(), { once: true });
              }),
          ),
        );
      try {
        await waitForLinks();
        if (doc.fonts && typeof doc.fonts.ready?.then === 'function') {
          await doc.fonts.ready;
        }
      } catch {
        // ignore
      }

      pw.focus();
      pw.print();
    } else {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      try {
        win.focus();
        win.print();
      } catch (err) {
        console.error('Iframe print failed:', err);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        {title}
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ height: '80vh' }}>
          <iframe
            ref={iframeRef}
            title="Print Preview"
            style={{
              width: '100%',
              height: '100%',
              border: 0,
              background: 'white',
              display: 'block',
            }}
            // Optional: show immediate content before CSS/Font wait completes
            srcDoc={docHtml}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handlePrint} variant="contained">
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}
